import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Heart, Info, Sparkles, Lock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';
import { recordApplication } from '../utils/applicationTracking';
import { useAIReachout, getReachoutUsage, FREE_REACHOUT_CREDITS } from '../hooks/useAIReachout';
import ConfidenceDonut from './ConfidenceDonut';

/**
 * MobileOfferDrawer - LinkedIn-style bottom sheet for offer details
 * - Slides up from bottom
 * - Can be dismissed by swiping down when scrolled to top
 * - Locks body scroll when open
 * - Includes "Candidature Trouve+" section
 * - Handles keyboard visibility for input fields
 */
const MobileOfferDrawer = ({ isOpen, onClose, offer, isFavorite, onToggleFavorite }) => {
    const contentRef = useRef(null);
    const inputRef = useRef(null);
    const candidatureSectionRef = useRef(null);
    const [isAtTop, setIsAtTop] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [iaMessage, setIaMessage] = useState('');
    const { user } = useAuth();
    const { generateReachout, isGenerating } = useAIReachout();
    const navigate = useNavigate();

    // Offer Insights State
    const [offerInsights, setOfferInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [hasInsights, setHasInsights] = useState(false);

    // Suit l'offre pour laquelle une génération IA est en cours : si l'utilisateur
    // change d'offre avant la fin de l'appel, on ignore le résultat à son retour
    // au lieu de l'afficher sur la mauvaise offre.
    const activeOfferIdRef = useRef(offer?.id);

    // Restaure le mail IA déjà généré pour cette offre (persisté en base) au lieu
    // de le vider : sinon un F5 ou un changement d'offre le fait perdre.
    useEffect(() => {
        const requestedOfferId = offer?.id;
        activeOfferIdRef.current = requestedOfferId;
        setIaMessage('');

        if (!user || !requestedOfferId || offer?.isExclusive) return;
        supabase
            .from('user_offer_activities')
            .select('ai_reachout_message')
            .eq('user_id', user.id)
            .eq('offer_id', requestedOfferId)
            .maybeSingle()
            .then(({ data }) => {
                if (activeOfferIdRef.current === requestedOfferId && data?.ai_reachout_message) {
                    setIaMessage(data.ai_reachout_message);
                }
            });
    }, [offer?.id, offer?.isExclusive, user]);

    // Auto-agrandit le textarea du mail IA pour afficher tout le texte généré
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    }, [iaMessage]);

    const handleAiGenerate = async () => {
        if (isGenerating || !user) return;

        const requestedOfferId = offer.id;
        const generatedText = await generateReachout(user.id, offer);

        // L'utilisateur a changé d'offre pendant la génération : on jette le résultat.
        if (activeOfferIdRef.current !== requestedOfferId) return;

        if (generatedText) {
            setIaMessage(generatedText);
            setUsageCount(c => c + 1);

            // Ajoute systématiquement l'offre aux favoris et persiste le texte généré,
            // pour qu'il ne soit pas perdu si l'utilisateur quitte la page ou fait F5.
            if (!isFavorite) onToggleFavorite(offer.id);
            supabase
                .from('user_offer_activities')
                .upsert({
                    user_id: user.id,
                    offer_id: requestedOfferId,
                    is_favorite: true,
                    is_viewed: true,
                    ai_reachout_message: generatedText,
                    updated_at: new Date(),
                }, { onConflict: 'user_id, offer_id' })
                .then(({ error: upsertError }) => {
                    if (upsertError) console.error('Error saving AI reachout message:', upsertError);
                });
        } else if (error) {
            alert(error); // Add error alert here as well to notify users
        }
    };

    // Premium & Tracking State
    const [isPremium, setIsPremium] = useState(false);
    const [usageCount, setUsageCount] = useState(0);
    const [isLoadingUsage, setIsLoadingUsage] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // Fetch Premium Status & Usage
    useEffect(() => {
        const fetchStatus = async () => {
            if (!user) return;

            // Check Premium
            const { data } = await supabase.from('profiles').select('is_premium').eq('id', user.id).single();
            const premiumStatus = data?.is_premium || false;
            setIsPremium(premiumStatus);

            // Check remaining free reachout credits if Premium
            if (premiumStatus) {
                setIsLoadingUsage(true);
                const { count } = await getReachoutUsage(user.id);
                setUsageCount(count);
                setIsLoadingUsage(false);
            }
        };
        if (isOpen) fetchStatus();
    }, [user, isOpen, offer?.id]);

    // Fetch offer insights (Premium) OR check existence (Non-Premium)
    useEffect(() => {
        if (!offer?.id || !isOpen) return;

        const fetchInsights = async () => {
            setInsightsLoading(true);
            try {
                if (isPremium) {
                    // Premium: Get full data
                    const { data, error } = await supabase.rpc('get_offer_insights', {
                        p_id_offre: offer.id
                    });
                    if (error) throw error;
                    setOfferInsights(data);
                    // Only show if confidence > 70
                    setHasInsights(!!data && data.confidence_score > 70);
                } else {
                    // Non-Premium: Check existence
                    const { data, error } = await supabase.rpc('get_offer_has_insights', {
                        p_id_offre: offer.id
                    });
                    if (error) {
                        console.warn("Could not check insights existence (RPC missing?)", error);
                        setHasInsights(false); // Default hidden if check fails
                    } else {
                        setHasInsights(data);
                    }
                }
            } catch (err) {
                console.error('Error fetching offer insights:', err);
                setOfferInsights(null);
                setHasInsights(false);
            } finally {
                setInsightsLoading(false);
            }
        };
        fetchInsights();
    }, [offer?.id, isOpen, isPremium]);

    // Handle "Envoyer votre profil"
    const handleApply = async () => {
        if (!isPremium || isSending) return;

        setIsSending(true);
        const { success, message, newCount } = await recordApplication(user.id, offer.id);

        if (success) {
            setUsageCount(newCount);
            alert(message); // Replace with a nice toast if available
            setIaMessage(''); // Clear input
        } else {
            alert(message);
        }
        setIsSending(false);
    };

    // Lock body scroll when drawer is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Hardware back button support
    useEffect(() => {
        if (isOpen) {
            window.history.pushState({ drawerOpen: true }, '');
            const handlePopState = () => {
                onClose();
            };
            window.addEventListener('popstate', handlePopState);
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [isOpen, onClose]);

    // Track if content is scrolled to top
    const handleScroll = () => {
        if (contentRef.current) {
            setIsAtTop(contentRef.current.scrollTop <= 5);
        }
    };

    // Handle touch events for swipe-to-dismiss
    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
        if (!touchStart) return;

        const currentY = e.touches[0].clientY;
        const diff = currentY - touchStart;

        // If at top and swiping down, prevent scroll and prepare to close
        if (isAtTop && diff > 0) {
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e) => {
        if (!touchStart) return;

        const endY = e.changedTouches[0].clientY;
        const diff = endY - touchStart;

        // If at top and swiped down more than 80px, close drawer
        if (isAtTop && diff > 80) {
            onClose();
        }

        setTouchStart(null);
    };

    if (!offer) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/50 z-[80] md:hidden"
                        onClick={onClose}
                    />

                    {/* Drawer */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[90] max-h-[90vh] overflow-hidden md:hidden"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Drag Handle */}
                        <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
                            <div className="w-10 h-1 bg-slate-300 rounded-full" />
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-10"
                        >
                            <X size={20} />
                        </button>

                        {/* Content - scrollable */}
                        <div
                            ref={contentRef}
                            onScroll={handleScroll}
                            className="px-5 pb-8 overflow-y-auto max-h-[calc(90vh-60px)] overscroll-contain"
                        >
                            {/* Company Badge */}
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                                    {(offer.company && typeof offer.company === 'string') ? offer.company.substring(0, 2).toUpperCase() : '?'}
                                </div>
                                <span className="text-sm font-semibold text-slate-600">{offer.company || 'Entreprise inconnue'}</span>
                            </div>

                            {/* Title */}
                            <h2 className="text-xl font-bold text-slate-900 mb-2 pr-8">
                                {offer.title}
                            </h2>

                            {/* Location & Date */}
                            <p className="text-sm text-slate-500 mb-4">
                                {offer.city}, {offer.country} · {offer.date}
                            </p>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2 mb-6">
                                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                    {offer.duration}
                                </span>
                                <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium">
                                    {offer.sector}
                                </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 mb-6">
                                <a
                                    href={offer.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => track('offer_clicked_external', { offer_id: offer.id, source: offer.isExclusive ? 'hors_bf' : 'bf', device: 'mobile' })}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white rounded-xl text-sm font-bold hover:bg-[#1e293b] active:scale-[0.98] transition-all"
                                >
                                    <ExternalLink size={16} />
                                    Redirection vers l'offre
                                </a>
                                <button
                                    onClick={() => onToggleFavorite(offer.id)}
                                    className={`px-4 py-3 rounded-xl text-sm font-bold border-2 transition-all active:scale-[0.98] ${isFavorite
                                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                                        : 'bg-white border-slate-300 text-slate-700 hover:border-blue-500'
                                        }`}
                                >
                                    <Heart size={18} className={isFavorite ? 'fill-blue-500' : ''} />
                                </button>
                            </div>

                            {/* Description Sections */}
                            <div className="space-y-6 mb-6">
                                {/* Mission */}
                                {offer.missionHtml && (
                                    <section>
                                        <h3 className="text-sm font-bold text-slate-900 mb-2">Mission</h3>
                                        <div
                                            className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{ __html: offer.missionHtml }}
                                        />
                                    </section>
                                )}

                                {/* Profile */}
                                {offer.profileHtml && (
                                    <section>
                                        <h3 className="text-sm font-bold text-slate-900 mb-2">Profil recherché</h3>
                                        <div
                                            className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{ __html: offer.profileHtml }}
                                        />
                                    </section>
                                )}

                                {/* Company */}
                                {offer.companyHtml && (
                                    <section>
                                        <h3 className="text-sm font-bold text-slate-900 mb-2">L'entreprise</h3>
                                        <div
                                            className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{ __html: offer.companyHtml }}
                                        />
                                    </section>
                                )}

                                {/* Fallback: Raw Description */}
                                {!offer.missionHtml && !offer.profileHtml && offer.description && (
                                    <section>
                                        <h3 className="text-sm font-bold text-slate-900 mb-2">Description</h3>
                                        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                                            {offer.description}
                                        </p>
                                    </section>
                                )}
                            </div>

                            {/* ========== Candidature Trouve+ Section ========== */}
                            {(!offer.isExclusive) && (
                                <div ref={candidatureSectionRef} className="bg-violet-400/15 rounded-2xl p-5 relative mb-8 border border-violet-200">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center justify-center px-3 py-1.5 bg-slate-900 rounded-full shadow-sm">
                                                <img
                                                    src="/assets/Trouve+white.png"
                                                    alt="Trouve+"
                                                    className="h-5 w-auto object-contain"
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="font-extrabold text-violet-700 text-base leading-tight">Candidature simplifiée</span>
                                                {isPremium && !isLoadingUsage && (
                                                    <span className="text-[10px] font-bold text-violet-400">
                                                        {usageCount >= FREE_REACHOUT_CREDITS
                                                            ? 'Essais gratuits épuisés'
                                                            : `${FREE_REACHOUT_CREDITS - usageCount} essai${FREE_REACHOUT_CREDITS - usageCount > 1 ? 's' : ''} gratuit${FREE_REACHOUT_CREDITS - usageCount > 1 ? 's' : ''} restant${FREE_REACHOUT_CREDITS - usageCount > 1 ? 's' : ''}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <button onClick={() => setShowInfo(!showInfo)} className="focus:outline-none p-1">
                                                <Info className={`w-5 h-5 transition-colors ${showInfo ? 'text-violet-600' : 'text-slate-400'}`} />
                                            </button>

                                            {showInfo && (
                                                <div className="absolute bottom-full right-0 mb-3 w-[260px] bg-white rounded-xl p-4 shadow-xl border border-gray-100 z-50 text-left">
                                                    <div className="text-xs text-[#64748b] space-y-2 font-medium leading-relaxed">
                                                        <p>
                                                            Notre IA rédige instantanément un message de candidature sur-mesure. Elle s'appuie sur votre CV et les prérequis de l'offre pour maximiser vos chances de réussite.
                                                        </p>
                                                        {/* <p className="font-bold text-[#334155]">
                                                            Un e-mail sera envoyé au recruteur avec votre CV.
                                                        </p> */}
                                                    </div>
                                                    <div className="absolute -bottom-2 right-2 w-3 h-3 bg-white border-b border-r border-gray-100 transform rotate-45"></div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Input Area */}
                                    <div className="flex flex-col gap-3">
                                        {isPremium ? (
                                            <div className="bg-white rounded-xl p-2 pl-3 flex items-end gap-2 shadow-sm border border-gray-200">
                                                <textarea
                                                    ref={inputRef}
                                                    rows={1}
                                                    placeholder={
                                                        usageCount >= FREE_REACHOUT_CREDITS
                                                            ? "Tu as utilisé tes essais gratuits de candidature simplifiée"
                                                            : (isGenerating ? "L'IA rédige..." : "Génère un mail de reach-out optimisé pour les VIE grâce à ton CV et à notre IA")
                                                    }
                                                    className="flex-1 bg-transparent text-xs text-slate-700 placeholder-slate-400 focus:outline-none py-2 resize-none max-h-[200px] overflow-y-auto leading-relaxed"
                                                    value={iaMessage}
                                                    onChange={(e) => setIaMessage(e.target.value)}
                                                    disabled={isGenerating || usageCount >= FREE_REACHOUT_CREDITS}
                                                    onFocus={() => {
                                                        // Scroll candidature section into view when keyboard opens
                                                        setTimeout(() => {
                                                            candidatureSectionRef.current?.scrollIntoView({
                                                                behavior: 'smooth',
                                                                block: 'end'
                                                            });
                                                        }, 300);
                                                    }}
                                                />
                                                <button
                                                    onClick={handleAiGenerate}
                                                    disabled={isGenerating || usageCount >= FREE_REACHOUT_CREDITS}
                                                    className={`
                                                    h-10 w-10 flex items-center justify-center rounded-[14px] transition-all flex-shrink-0 relative overflow-hidden group
                                                    ${isGenerating
                                                            ? 'bg-[#0F172A] shadow-[0_0_15px_rgba(139,92,246,0.6)] border border-violet-500/50 animate-pulse cursor-wait'
                                                            : (usageCount >= FREE_REACHOUT_CREDITS ? 'bg-gray-100/80 text-gray-300 cursor-not-allowed' : (iaMessage ? 'bg-[#0F172A] text-white hover:bg-black shadow-md' : 'bg-gray-100/80 text-gray-400 hover:bg-gray-200 hover:text-gray-600'))
                                                        }
                                                `}
                                                >
                                                    <svg width="0" height="0" className="absolute">
                                                        <defs>
                                                            <linearGradient id="ai-gradient-mobile" x1="0%" y1="0%" x2="100%" y2="100%">
                                                                <stop offset="0%" stopColor="#8b5cf6" />
                                                                <stop offset="100%" stopColor="#3b82f6" />
                                                            </linearGradient>
                                                        </defs>
                                                    </svg>
                                                    <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full transition-all duration-1000 ${isGenerating ? 'animate-[shimmer_1.5s_infinite]' : 'group-hover:animate-[shimmer_1.5s_infinite]'}`} />
                                                    <Sparkles
                                                        className={`w-4 h-4 relative z-10 transition-all ${isGenerating ? 'scale-110 drop-shadow-[0_0_5px_rgba(139,92,246,0.8)]' : ''}`}
                                                        strokeWidth={isGenerating ? 2.5 : 2}
                                                        style={isGenerating ? { stroke: "url(#ai-gradient-mobile)", color: "transparent" } : {}}
                                                    />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="px-2 mb-1">
                                                <p className="text-xs text-slate-600 font-medium leading-relaxed text-justify">
                                                    <span className="font-bold text-violet-700">L'IA Trouve+ rédige pour vous</span> un email optimisé.
                                                    {/* <br />
                                                    Nous envoyons <span className="font-bold">votre profil, CV et coordonnées</span> au recruteur. */}
                                                </p>
                                            </div>
                                        )}

                                        {isPremium ? (
                                            null /* Temporarily disabled
                                            <button
                                                onClick={handleApply}
                                                disabled={usageCount >= 5 || isSending}
                                                className={`w-full text-white text-sm font-bold py-3 px-4 rounded-xl shadow-lg transition-all active:scale-[0.98]
                                                ${usageCount >= 5
                                                        ? 'bg-slate-400 cursor-not-allowed'
                                                        : 'bg-[#0F172A] hover:bg-[#1e293b]'
                                                    }
                                            `}
                                            >
                                                {isSending ? "Envoi..." : (usageCount >= 5 ? "Limite atteinte (5/5)" : `Envoyer votre profil (${usageCount}/5)`)}
                                            </button>
                                            */
                                        ) : (
                                            <button
                                                onClick={() => navigate('/trouve-plus')}
                                                className="w-full bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-bold py-3 px-4 rounded-xl shadow-lg transition-all active:scale-[0.98]"
                                            >
                                                Débloquer avec Trouve+
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ===== INSIGHTS SECTION ===== */}
                            {hasInsights && (
                                <div className="bg-gradient-to-br from-blue-50/80 to-violet-50/80 rounded-2xl p-5 mt-6 border border-violet-100/40">
                                    {/* Header */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex items-center justify-center px-3 py-1.5 bg-slate-900 rounded-full">
                                            <img src="/assets/Trouve+white.png" alt="Trouve+" className="h-4 w-auto object-contain" />
                                        </div>
                                        <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 text-base">
                                            Insights
                                        </span>
                                    </div>

                                    {!isPremium ? (
                                        /* Non-premium locked state */
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-start gap-3 py-1">
                                                <div className="w-9 h-9 bg-white/80 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <Lock className="w-4 h-4 text-slate-400" />
                                                </div>
                                                <p className="text-sm text-slate-700 font-medium leading-snug flex-1">
                                                    Découvrez comment cette entreprise recrute et identifiez les meilleurs contacts LinkedIn.
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => navigate('/trouve-plus')}
                                                className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-1.5"
                                            >
                                                <span>Débloquer avec</span>
                                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-violet-300 font-extrabold">Trouve+</span>
                                            </button>
                                        </div>
                                    ) : insightsLoading ? (
                                        <div className="flex items-center justify-center py-4">
                                            <div className="w-5 h-5 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                                        </div>
                                    ) : (
                                        /* Has insights (guaranteed due to hasInsights) */
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1">
                                                <p className="text-xs text-slate-700 leading-relaxed">
                                                    Les recrutements de VIE sont gérés par des{' '}
                                                    <span className="font-bold text-violet-700">{offerInsights?.contact_linkedin_supposed_role_type}</span>
                                                    {offerInsights?.contact_location && (
                                                        <span>
                                                            {offerInsights.contact_location === 'France'
                                                                ? ' basés en France (siège)'
                                                                : ' basés en local (pays du VIE)'}
                                                        </span>
                                                    )}.
                                                </p>
                                                <p className="text-xs text-slate-700 leading-relaxed mt-2">
                                                    Pour un reachout sur LinkedIn, nous vous conseillons de contacter les{' '}
                                                    <span className="font-bold text-violet-700">
                                                        {offerInsights?.contact_linkedin_supposed_role_type}
                                                        {offerInsights?.contact_linkedin_supposed_role && (
                                                            <span className="font-semibold text-slate-600"> ({offerInsights.contact_linkedin_supposed_role})</span>
                                                        )}
                                                    </span>{' '}
                                                    chez{' '}
                                                    <span className="font-bold text-slate-900">{offerInsights?.search_company_name || offer.company}</span>.
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0 relative">
                                                <ConfidenceDonut
                                                    score={offerInsights?.confidence_score}
                                                    size={60}
                                                    strokeWidth={6}
                                                    label="Confiance"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default MobileOfferDrawer;
